<template>
  <v-container fluid class="planogram-container">
    <!-- Header Controls -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-view-grid</v-icon>
        Visual Merchandising - Planogramma
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="auto">
            <v-btn 
              color="primary" 
              @click="addShelf"
              :disabled="!cabinetConfig.width || !cabinetConfig.height"
            >
              <v-icon left>mdi-plus</v-icon>
              Aggiungi Scaffale
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn 
              color="secondary" 
              @click="clearPlanogram"
              :disabled="shelves.length === 0"
            >
              <v-icon left>mdi-delete</v-icon>
              Svuota
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn 
              color="success" 
              @click="savePlanogram()"
              :disabled="shelves.length === 0"
            >
              <v-icon left>mdi-content-save</v-icon>
              Salva
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn 
              color="info" 
              @click="exportPlanogram('pdf')"
              :disabled="shelves.length === 0"
            >
              <v-icon left>mdi-download</v-icon>
              Esporta
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-text-field
              v-model="cabinetConfig.width"
              label="Larghezza Armadio (cm)"
              type="number"
              min="50"
              max="500"
              style="max-width: 200px"
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-text-field
              v-model="cabinetConfig.height"
              label="Altezza Armadio (cm)"
              type="number"
              min="100"
              max="400"
              style="max-width: 200px"
              dense
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row>
      <!-- Product List Panel -->
      <v-col cols="3">
        <PlanogramProductList 
          ref="productList"
          @start-drag="onStartDrag"
          @end-drag="onEndDrag"
        />
      </v-col>

      <!-- Main Workspace -->
      <v-col cols="9">
        <v-card class="planogram-workspace" min-height="600">
          <v-card-title class="py-2">
            <v-icon left>mdi-store</v-icon>
            Area di Lavoro - Armadio
            <v-spacer></v-spacer>
            <small v-if="cabinetConfig.width && cabinetConfig.height">
              {{ cabinetConfig.width }}cm x {{ cabinetConfig.height }}cm
            </small>
          </v-card-title>
          
          <!-- Cabinet Container -->
          <div 
            class="cabinet-container"
            :style="cabinetStyle"
            @dragover="allowDrop"
            @drop="onDropToCabinet"
          >
            <!-- Background Grid -->
            <div class="cabinet-grid"></div>
            
            <!-- Shelves -->
            <PlanogramShelf
              v-for="shelf in shelves"
              :key="shelf.id"
              :shelf="shelf"
              :is-dragging="isDragging"
              @update-shelf="updateShelf"
              @delete-shelf="deleteShelf"
              @drop-product="onDropToShelf"
            />
            
            <!-- Drop Zone Indicator -->
            <div 
              v-if="isDragging" 
              class="drop-zone-indicator"
              :class="{ 'drop-zone-active': isValidDropZone }"
            >
              <v-icon size="48" color="primary">mdi-package-variant</v-icon>
              <div class="mt-2">Rilascia il prodotto qui per creare un nuovo scaffale</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Shelf Configuration Dialog -->
    <v-dialog v-model="showShelfDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-shelf</v-icon>
          Configura Scaffale
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="newShelfConfig.length"
                label="Lunghezza (cm)"
                type="number"
                min="20"
                max="200"
                required
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="newShelfConfig.depth"
                label="Profondità (cm)"
                type="number"
                min="10"
                max="60"
                required
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="newShelfConfig.verticalPosition"
                label="Posizione Verticale (cm dal basso)"
                type="number"
                min="0"
                :max="cabinetConfig.height - 20"
                required
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showShelfDialog = false">Annulla</v-btn>
          <v-btn color="primary" @click="confirmAddShelf">Conferma</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import PlanogramProductList from './PlanogramProductList.vue'
import PlanogramShelf from './PlanogramShelf.vue'
import requests from '../../requests'

export default {
  name: 'Planogram',
  components: {
    PlanogramProductList,
    PlanogramShelf
  },
  data() {
    return {
      // Cabinet configuration
      cabinetConfig: {
        width: 200,
        height: 250
      },
      
      // Shelves array
      shelves: [],
      
      // Drag and drop state
      isDragging: false,
      isValidDropZone: false,
      draggedProduct: null,
      
      // Dialog state
      showShelfDialog: false,
      newShelfConfig: {
        length: 160,
        depth: 50,
        verticalPosition: 50
      },
      
      // Internal counter for shelf IDs
      shelfIdCounter: 0
    }
  },
  computed: {
    cabinetStyle() {
      if (!this.cabinetConfig.width || !this.cabinetConfig.height) {
        return {
          width: '100%',
          height: '400px',
          border: '2px dashed #ccc',
          borderRadius: '8px',
          position: 'relative',
          backgroundColor: '#fafafa'
        }
      }
      
      // Scale factor to fit cabinet in workspace (max 900px width, min scale 0.8)
      const maxWidth = 900
      const scale = Math.max(0.8, Math.min(1.2, maxWidth / this.cabinetConfig.width))
      
      return {
        width: `${this.cabinetConfig.width * scale}px`,
        height: `${this.cabinetConfig.height * scale}px`,
        border: '3px solid #1976d2',
        borderRadius: '8px',
        position: 'relative',
        backgroundColor: '#f8f9fa',
        margin: '20px auto',
        transform: `scale(${scale})`,
        transformOrigin: 'top center'
      }
    }
  },
  methods: {
    // Shelf management
    addShelf() {
      this.showShelfDialog = true
    },
    
    confirmAddShelf() {
      const newShelf = {
        id: ++this.shelfIdCounter,
        length: parseInt(this.newShelfConfig.length),
        depth: parseInt(this.newShelfConfig.depth),
        verticalPosition: parseInt(this.newShelfConfig.verticalPosition),
        horizontalPosition: 15, // Left alignment with small margin
        products: []
      }
      
      this.shelves.push(newShelf)
      this.showShelfDialog = false
      
      // Reset form
      this.newShelfConfig = {
        length: 160,
        depth: 50,
        verticalPosition: 50
      }
    },
    
    updateShelf(updatedShelf) {
      const index = this.shelves.findIndex(s => s.id === updatedShelf.id)
      if (index !== -1) {
        this.$set(this.shelves, index, updatedShelf)
      }
    },
    
    deleteShelf(shelfId) {
      const index = this.shelves.findIndex(s => s.id === shelfId)
      if (index !== -1) {
        this.shelves.splice(index, 1)
      }
    },
    
    clearPlanogram() {
      if (confirm('Sei sicuro di voler svuotare tutto il planogramma?')) {
        this.shelves = []
        this.shelfIdCounter = 0
      }
    },

    // Planogram persistence methods
    async savePlanogram(name = null) {
      const planogramName = name || prompt('Inserisci un nome per il planogramma:')
      if (!planogramName) return

      const planogramData = {
        name: planogramName,
        cabinet_config: this.cabinetConfig,
        shelves: this.shelves,
        created_at: new Date().toISOString()
      }

      try {
        const response = await requests.savePlanogram(planogramData)
        console.log('Planogram saved successfully:', response.data)
        // Show success message
        alert('Planogramma salvato con successo!')
      } catch (error) {
        console.error('Error saving planogram:', error)
        alert('Errore nel salvataggio del planogramma')
      }
    },

    async loadPlanogram(planogramId) {
      if (!planogramId) return

      try {
        const response = await requests.loadPlanogram(planogramId)
        const planogramData = response.data

        this.cabinetConfig = planogramData.cabinet_config || { width: 200, height: 250 }
        this.shelves = planogramData.shelves || []
        this.shelfIdCounter = Math.max(...this.shelves.map(s => s.id), 0)

        console.log('Planogram loaded successfully')
      } catch (error) {
        console.error('Error loading planogram:', error)
        alert('Errore nel caricamento del planogramma')
      }
    },

    async exportPlanogram(format = 'pdf') {
      if (this.shelves.length === 0) {
        alert('Non ci sono scaffali da esportare')
        return
      }

      // For now, just save and show success - export functionality can be implemented later
      try {
        await this.savePlanogram('Export_' + new Date().getTime())
        alert(`Planogramma pronto per l'esportazione in formato ${format.toUpperCase()}`)
      } catch (error) {
        console.error('Error exporting planogram:', error)
        alert('Errore nell\'esportazione del planogramma')
      }
    },
    
    // Drag and drop handling
    onStartDrag(product) {
      this.isDragging = true
      this.draggedProduct = product
    },
    
    onEndDrag() {
      this.isDragging = false
      this.isValidDropZone = false
      this.draggedProduct = null
    },
    
    allowDrop(event) {
      event.preventDefault()
      this.isValidDropZone = this.isDragging
    },
    
    onDropToCabinet(event) {
      event.preventDefault()
      if (!this.isDragging || !this.draggedProduct) return
      
      // Create a new shelf at drop position
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      
      // Convert to cabinet coordinates
      const scale = Math.max(0.8, Math.min(1.2, 900 / this.cabinetConfig.width))
      const cabinetX = x / scale
      const cabinetY = this.cabinetConfig.height - (y / scale)
      
      // Create shelf with product
      const newShelf = {
        id: ++this.shelfIdCounter,
        length: Math.max((this.draggedProduct.larghezza || 40) * 1.5 + 60, 140), // Account for visual scaling + padding
        depth: Math.max(this.draggedProduct.profondita + 20, 50),
        verticalPosition: Math.max(0, Math.min(cabinetY - 10, this.cabinetConfig.height - 30)),
        horizontalPosition: 15, // Align to left side consistently
        products: [this.draggedProduct]
      }
      
      this.shelves.push(newShelf)
      this.onEndDrag()
    },
    
    onDropToShelf(shelfId, product) {
      const shelf = this.shelves.find(s => s.id === shelfId)
      if (shelf && !shelf.products.find(p => p.id === product.id)) {
        shelf.products.push(product)
      }
      this.onEndDrag()
    }
  }
}
</script>

<style scoped>
.planogram-container {
  height: 100vh;
  overflow: hidden;
}

.planogram-workspace {
  height: calc(100vh - 120px);
  overflow: auto;
}

.cabinet-container {
  position: relative;
  overflow: hidden;
  background-image: 
    linear-gradient(rgba(0,0,0,.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,.1) 1px, transparent 1px);
  background-size: 20px 20px;
  min-height: 400px;
}

.cabinet-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(25,118,210,.2) 1px, transparent 1px),
    linear-gradient(90deg, rgba(25,118,210,.2) 1px, transparent 1px);
  background-size: 20px 20px;
  pointer-events: none;
}

.drop-zone-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #666;
  pointer-events: none;
  opacity: 0.7;
  transition: all 0.3s ease;
}

.drop-zone-active {
  opacity: 1;
  color: #1976d2;
  transform: translate(-50%, -50%) scale(1.1);
}
</style>